<script>
    import { onMount } from 'svelte';
    import MultiSelect from "../../MultiSelect.svelte";
    import Modal, { getModal } from '../../modals/OnMountModal.svelte';
    import feedStore from '../../../stores/feed';
    import { queryGroupsWithItem } from '../../../stores/entities';
    import { saveItems } from '../../../storagePersistence/index';
    export let showItemManager;
    export let item;
    export let activeFeed;
    export let updateLocalItem;
    export let subscriptions;
    let updatedGroupIDs;
    let linkTagIDs;
    let itemTagIDs;
    let linkTags = null;
    let itemLink;

    $: roleValue = $feedStore.roleValue;
    $: currentUserID = $feedStore.currentUserID;
    $: itemTagsMap = $feedStore.itemTagsMap;
    $: tagOptions = Object.keys(itemTagsMap).map(tagName => ({ id: tagName, name: tagName }))

    $: itemUser = item.user;
    $: initialItemTags = item.tags || [];
    $: itemAndGroupIDs = queryGroupsWithItem(item.id);
    $: [itemGroupIDs, allItemIDs, groupToItemMap] = itemAndGroupIDs.reduce((acc, [groupID, itemID]) => {
        acc[0].push(groupID);
        acc[1].push(itemID);
        acc[2][groupID] = itemID;
        return acc;
    }, [[], [], {}]);
    $: canManageItem = itemUser && itemUser.id === currentUserID;

    onMount(() => {
        getModal().open(showItemManager);

        itemLink = item.link;

        if (itemLink && roleValue >= 4) {
            fetchLinkTags();
        }
    })

    /**
     * feedItems can be added to multiple groups -> create editionItem for each group.
     * editionItems can be added to multiple groups -> 
     *      input: itemLink, item.tags, userID; 
     *      result: create editionItem for each group and use the input tags
     * Tags can be added to feedItems -> result is that a new item will be created in bin and tagged.
     *      input: itemLink, userID
     *      result: 
     * Tags can be added to editionItems -> result is that every one of the user's items that share the same link will get the tag
     *      input: itemLink, userID, 
     * 
     * 
     * 
     * Potential orders of operations: (can both end in the same result?)
     * - first add tags then add to groups
     * - first add to groups then add tags
    */

    function getTagNames() {
        return (itemTagIDs || []).map(tag => tag.startsWith('+') ? `#${tag.substring(1)}` : tag);
    }

    function buildItemBody(tagsToAdd = [], tagsToRemove = []) {
        // const tagNames = getTagNames();
        let itemBody = (item.textBody || '');
        if (itemLink) {
            itemBody += ` ${itemLink}`
        }
        if (!itemBody) {
            return;
        }
        if (initialItemTags) {
            itemBody += ` ${initialItemTags.join(' ')}`
        }
        if (tagsToAdd) {
            itemBody += ` ${tagsToAdd.join(' ')}`
        }
        if (tagsToRemove) {
            // TODO: regex and remove any tags in tagsToRemove
            itemBody = itemBody.replace(
                new RegExp(tagsToRemove.join("|"), "gi"), 
                () => ''
            );
        }
        return itemBody.trim();
    }

    const addItemToGroup = () => {
        const tagNames = getTagNames();
        const itemBody = buildItemBody(tagNames);
        if (!itemBody) return;
        fetch(`/api/v1/add-item`, {
            body: JSON.stringify({
                updatedGroupIDs,
                linkUrl: itemBody,
            }),
            method: 'POST',
            'content-type': 'application/json',
        })
        .then(data => data.json())
        .then(res => {
            const { success } = res;
            if (success) {
                getModal().close()
            }
        })
    }

    const deleteItem = () => {
        fetch(`/api/v1/delete-item`, {
            body: JSON.stringify({
                itemID: item.id,
            }),
            method: 'POST',
            'content-type': 'application/json',
        })
        .then(data => data.json())
        .then(res => {
            const { countDeleted } = res;
            if (countDeleted > 0) {
                getModal().close()
            }
        })
    }

    function fetchLinkTags() {
        fetch(`/api/v1/link-tags/get`, {
            body: JSON.stringify({
                link: item.link
            }),
            method: 'POST',
        })
        .then(data => data.json())
        .then(res => {
            const { tags = [] } = res;
            linkTags = tags;
        })
    }

    // only allow me to add tags if I've saved the item to one of my groups.
    // admin, allow me to update existing link tags
    // If i have already saved the item to one of my groups, allow me to add tags, allow me to change groups
    // always allow role >= 4 to save tags directly to the link
    // role == 4 cannot update existing link tags

    // what can only be done to your own item vs someone else's item?

    function addTagsToLink() {
        const tagNames = linkTagIDs.map(tag => tag.startsWith('+') ? `#${tag.substring(1)}` : tag);
        fetch('/api/v1/link-tags/add', {
            body: JSON.stringify({
                linkTagIDs: tagNames,
                link: item.link,
            }),
            method: 'POST',
            'content-type': 'application/json',
        })
        .then(data => data.json())
        .then(res => {
            const { success } = res;
            if (success) {
                feedStore.updateAddItemTags({ itemID: item.id, tagNames });
            }
        })
    }

    function addTagsToItem() {
        const tagNames = getTagNames();
        fetch('/api/v1/item-tags/add', {
            body: JSON.stringify({
                itemTagIDs: tagNames,
                itemID: item.id,
            }),
            method: 'POST',
            'content-type': 'application/json',
        })
        .then(data => data.json())
        .then(res => {
            const { success } = res;
            if (success) {
                feedStore.updateAddItemTags({ itemID: item.id, tagNames });
            }
        })
    }

    function buildSavePayload(updatedTagNames) {
        // tagsToAdd
        // tagsToRemove
        // groupsToAdd
        // groupsToRemove
        let response = {
            groupsToAdd: updatedGroupIDs.filter(groupID => !(new Set(itemGroupIDs)).has(groupID)),
            groupsToRemove: itemGroupIDs.filter(groupID => !(new Set(updatedGroupIDs)).has(groupID)),
            currentItemGroups: groupToItemMap,
            tagsToAdd: updatedTagNames.filter(tagName => !(new Set(initialItemTags)).has(tagName)),
            
            // remove these tags from all item/group associations
            tagsToRemove: initialItemTags.filter(tagName => !(new Set(updatedTagNames)).has(tagName)),
        }

        if (response.groupsToAdd.length > 0) {
            response = {
                ...response,
                itemBody: buildItemBody(response.tagsToAdd, response.tagsToRemove)
            }
        }

        return response;
    }

    function _updateLocalItem({
        updatedTagNames,
        newItem,
    }) {
        let updateRequestObject = {}
        if (updatedTagNames) {
            updateRequestObject = {
                ...updateRequestObject,
                updatedTagNames,
            }
        }
        if (!item.editionID && newItem) {
            updateRequestObject = {
                ...updateRequestObject,
                newItem,
            }
        }

        updateLocalItem(updateRequestObject)
    }

    async function save() {
        const updatedTagNames = getTagNames();
        _updateLocalItem({ updatedTagNames });
        let payload = buildSavePayload(updatedTagNames);

        const saveItemsResponse = await saveItems(payload);

        const { newItems } = saveItemsResponse;

        if (newItems?.length) {
            _updateLocalItem({ newItem: newItems[0]})
        }

        // if a feed item is being replaced with a subscriptionItem

        // if save was unsuccessful, rollback changes.
    }

    function copyTagsToLink(e) {
        linkTagIDs = itemTagIDs || [];
    }
</script>

<Modal class="action-modal">
    <h2>{item.title}</h2>

    {#if subscriptions.length > 0}
        <label for='subscription-multiselect'>Groups</label>
        <MultiSelect id='subscription-multiselect' bind:value={updatedGroupIDs} initialValue={itemGroupIDs}>
            <option selected="false" style='display: none' hidden disabled value=""></option>
            {#each subscriptions as subscription}
                <option value={subscription.id}>{subscription.name}</option>
            {/each}
        </MultiSelect>

        {#if canManageItem}
            <MultiSelect id='item-tag-multiselect'
                bind:value={itemTagIDs}
                canAddNewOptions={true}
                initialValue={initialItemTags}
                disabled={!itemGroupIDs || itemGroupIDs.length === 0}
                placeholder="add tags"
            >
                <option selected="false" style='display: none' hidden disabled value=""></option>
                {#each tagOptions as tag}
                    <option value={tag.id}>{tag.name}</option>
                {/each}
            </MultiSelect>
        {/if}
    {/if}

    <button
        class="primary-green full-width margin-top-sm"
        on:click={save}>
        Save
    </button>

    {#if canManageItem}
        {#if activeFeed === 'mySubscription'}
            <button class="full-width" on:click="{deleteItem}">Delete</button>
        {/if}
    {/if}

    {#if item.link && roleValue >= 4}
        {#if linkTags === null}
            <div>loading...</div>
        {:else}
            <div class='tag-explanation'>
                <h3>You are a curator.</h3>
                <p>
                    You can choose to add tags to the link associated with this content.
                    This helps provide default tags to others who might not have added tags yet.
                </p>

                {#if canManageItem}
                    <div>
                        <button on:click={copyTagsToLink}>add tags to link</button>
                    </div>
                {/if}

            </div>
            <MultiSelect id='link-tag-multiselect' bind:value={linkTagIDs} canAddNewOptions={true} initialValue={linkTags}>
                <option selected="false" style='display: none' hidden disabled value=""></option>
                {#each tagOptions as tag}
                    {#if tag.id}
                        <option value={tag.id}>{tag.name}</option>
                    {:else}
                        <option value={`+${tag.name}`}>{tag.name}</option>
                    {/if}
                {/each}
            </MultiSelect>
            <form id='tag-link-form'>
                <button on:click|preventDefault={() => addTagsToLink()} type="submit" form="tag-link-form" 
                    class="primary-green full-width margin-top-sm">
                    Tag Link
                </button>
            </form>
        {/if}
    {/if}
</Modal>

<style>
    .tag-explanation {
        text-align: center;
        margin: 40px 0 10px;
    }
</style>